import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchMarketIndexCatalog,
  fetchMarketIndexCoverage,
  fetchMarketIndexHistory,
  saveManualMarketIndexRow,
  getDecisionApiError
} from '../../../services/decisionApi';

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(4)}%`;
}

function formatClose(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(4);
}

export default function MarketIndexManualPanel() {
  const [catalog, setCatalog] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [history, setHistory] = useState([]);
  const [indexCode, setIndexCode] = useState('');
  const [tradeDate, setTradeDate] = useState(todayIso());
  const [changePct, setChangePct] = useState('');
  const [close, setClose] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const coverageMap = useMemo(() => {
    const map = new Map();
    coverage.forEach((row) => map.set(row.index_code, row));
    return map;
  }, [coverage]);

  const selectedMeta = useMemo(
    () => catalog.find((item) => item.index_code === indexCode) || null,
    [catalog, indexCode]
  );

  const refreshCoverage = useCallback(async () => {
    const data = await fetchMarketIndexCoverage();
    setCoverage(data.coverage || []);
  }, []);

  const refreshHistory = useCallback(async (code) => {
    if (!code) {
      setHistory([]);
      return;
    }
    const data = await fetchMarketIndexHistory(code, 15);
    setHistory(data.history || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [catalogData, coverageData] = await Promise.all([
          fetchMarketIndexCatalog(),
          fetchMarketIndexCoverage()
        ]);
        if (cancelled) return;
        const items = catalogData.catalog || [];
        setCatalog(items);
        setCoverage(coverageData.coverage || []);
        const first = items[0]?.index_code || '';
        setIndexCode((prev) => prev || first);
      } catch (err) {
        if (!cancelled) setError(getDecisionApiError(err, '加载指数目录失败'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!indexCode) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMarketIndexHistory(indexCode, 15);
        if (!cancelled) setHistory(data.history || []);
      } catch (err) {
        if (!cancelled) setError(getDecisionApiError(err, '加载历史失败'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [indexCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        index_code: indexCode,
        trade_date: tradeDate,
        change_pct: Number(changePct)
      };
      if (close !== '') payload.close = Number(close);
      await saveManualMarketIndexRow(payload);
      setMessage(`已保存 ${selectedMeta?.name || indexCode} · ${tradeDate}`);
      setChangePct('');
      setClose('');
      await refreshCoverage();
      await refreshHistory(indexCode);
    } catch (err) {
      setError(getDecisionApiError(err, '保存失败'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="decision-muted">加载指数目录…</p>;
  }

  return (
    <div className="decision-card market-index-manual-panel">
      <h3>指数手动补录</h3>
      <p className="decision-muted" style={{ marginBottom: 14 }}>
        自动拉取失败时，可在此录入指定交易日的涨跌幅（%）；写入后业绩比较基准会优先使用本地数据。
      </p>

      <form onSubmit={handleSubmit}>
        <div className="market-index-form-grid">
          <label>
            <div className="decision-muted">指数</div>
            <select
              className="decision-select"
              value={indexCode}
              onChange={(e) => setIndexCode(e.target.value)}
            >
              {catalog.map((item) => (
                <option key={item.index_code} value={item.index_code}>
                  {item.name} ({item.index_code})
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="decision-muted">交易日</div>
            <input
              className="decision-input"
              type="date"
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
              required
            />
          </label>
          <label>
            <div className="decision-muted">涨跌幅（%）</div>
            <input
              className="decision-input"
              type="number"
              step="0.0001"
              value={changePct}
              onChange={(e) => setChangePct(e.target.value)}
              placeholder="例如 -0.85"
              required
            />
          </label>
          <label>
            <div className="decision-muted">收盘价（可选）</div>
            <input
              className="decision-input"
              type="number"
              step="0.0001"
              value={close}
              onChange={(e) => setClose(e.target.value)}
              placeholder="留空则只存涨跌幅"
            />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <button type="submit" className="decision-btn" disabled={saving || !indexCode}>
            {saving ? '保存中…' : '保存补录'}
          </button>
        </div>
      </form>

      {message ? <p className="decision-muted" style={{ marginTop: 10 }}>{message}</p> : null}
      {error ? <p className="decision-error">{error}</p> : null}

      <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: 13, color: '#94a3b8' }}>入库覆盖</h4>
      <div className="decision-table-wrap">
        <table className="decision-table decision-table-static">
          <thead>
            <tr>
              <th>指数</th>
              <th>条数</th>
              <th>最新日期</th>
            </tr>
          </thead>
          <tbody>
            {catalog.map((item) => {
              const row = coverageMap.get(item.index_code);
              return (
                <tr key={item.index_code}>
                  <td>
                    {item.name}
                    <div className="decision-muted" style={{ fontSize: 11 }}>{item.index_code}</div>
                  </td>
                  <td>{row?.row_count ?? 0}</td>
                  <td>{row?.latest_date || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {indexCode ? (
        <>
          <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: 13, color: '#94a3b8' }}>
            最近记录 · {selectedMeta?.name || indexCode}
          </h4>
          {history.length ? (
            <div className="decision-table-wrap">
              <table className="decision-table decision-table-static">
                <thead>
                  <tr>
                    <th>交易日</th>
                    <th>涨跌幅</th>
                    <th>收盘</th>
                    <th>MA20</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history].reverse().map((row) => (
                    <tr key={`${row.index_code}-${row.trade_date}`}>
                      <td>{row.trade_date}</td>
                      <td>{formatPct(row.change_pct)}</td>
                      <td>{formatClose(row.close)}</td>
                      <td>{row.ma20 != null ? formatClose(row.ma20) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="decision-muted">暂无历史记录</p>
          )}
        </>
      ) : null}
    </div>
  );
}
